import type { TypedSupabaseClient } from '../client';
import type {
  IDeliveryProviderRepository,
  DeliveryProvider,
  CreateDeliveryProviderInput,
  UpdateDeliveryProviderInput,
  DeliveryProviderTransaction,
  CreateDeliveryTransactionInput,
  DeliveryTransactionFilters,
  DeliveryTransactionStatus,
  DeliveryWalletSummary,
  DeliveryProviderLog,
  CreateDeliveryLogInput,
  DeliveryLogFilters,
  DeliveryProviderCity,
  DeliveryProviderDistrict,
  DeliveryProviderNeighborhood,
  DeliveryProviderPickup,
  ReplaceCitiesInput,
  ReplaceDistrictsInput,
  ReplaceNeighborhoodsInput,
  ReplacePickupsInput,
} from '../../../interfaces/src';

const anyFrom = (client: TypedSupabaseClient) =>
  (table: string) => (client as unknown as { from: (t: string) => any }).from(table);

export class SupabaseDeliveryProvidersRepository implements IDeliveryProviderRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  // ---- Providers --------------------------------------------------------

  async list(): Promise<DeliveryProvider[]> {
    const { data, error } = await anyFrom(this.supabase)('delivery_providers')
      .select('*').order('display_name', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapProvider);
  }

  async getById(id: string): Promise<DeliveryProvider | null> {
    const { data, error } = await anyFrom(this.supabase)('delivery_providers')
      .select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapProvider(data) : null;
  }

  async getByCode(code: string): Promise<DeliveryProvider | null> {
    const { data, error } = await anyFrom(this.supabase)('delivery_providers')
      .select('*').eq('code', code).maybeSingle();
    if (error) throw error;
    return data ? mapProvider(data) : null;
  }

  async getDefault(): Promise<DeliveryProvider | null> {
    const { data, error } = await anyFrom(this.supabase)('delivery_providers')
      .select('*').eq('is_default', true).eq('is_enabled', true).maybeSingle();
    if (error) throw error;
    return data ? mapProvider(data) : null;
  }

  async create(input: CreateDeliveryProviderInput): Promise<DeliveryProvider> {
    const { data, error } = await anyFrom(this.supabase)('delivery_providers')
      .insert({
        code: input.code,
        display_name: input.displayName,
        is_enabled: input.isEnabled ?? false,
        is_default: input.isDefault ?? false,
        api_key: input.apiKey ?? null,
        webhook_secret: input.webhookSecret ?? null,
        business_id: input.businessId ?? null,
        default_pickup_index: input.defaultPickupIndex ?? null,
        default_fee: input.defaultFee ?? 0,
        default_package_size: input.defaultPackageSize ?? 1,
        config: input.config ?? {},
      })
      .select().single();
    if (error) throw error;
    return mapProvider(data);
  }

  async update(id: string, input: UpdateDeliveryProviderInput): Promise<DeliveryProvider> {
    const map: Record<string, string> = {
      displayName: 'display_name',
      isEnabled: 'is_enabled',
      isDefault: 'is_default',
      apiKey: 'api_key',
      webhookSecret: 'webhook_secret',
      businessId: 'business_id',
      defaultPickupIndex: 'default_pickup_index',
      defaultFee: 'default_fee',
      defaultPackageSize: 'default_package_size',
      config: 'config',
    };
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      const col = map[k];
      if (col) patch[col] = v;
    }
    const { data, error } = await anyFrom(this.supabase)('delivery_providers')
      .update(patch).eq('id', id).select().single();
    if (error) throw error;
    return mapProvider(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('delivery_providers').delete().eq('id', id);
    if (error) throw error;
  }

  async setDefault(id: string): Promise<void> {
    // Two-step to keep the unique partial index happy (one row at a time can hold is_default=true).
    const { error: e1 } = await anyFrom(this.supabase)('delivery_providers')
      .update({ is_default: false }).eq('is_default', true);
    if (e1) throw e1;
    const { error: e2 } = await anyFrom(this.supabase)('delivery_providers')
      .update({ is_default: true }).eq('id', id);
    if (e2) throw e2;
  }

  async setCachedBalance(id: string, available: number, pending: number): Promise<void> {
    const { error } = await anyFrom(this.supabase)('delivery_providers')
      .update({
        cached_balance_available: available,
        cached_balance_pending: pending,
        cached_balance_at: new Date().toISOString(),
      }).eq('id', id);
    if (error) throw error;
  }

  // ---- Transactions -----------------------------------------------------

  async listTransactions(filters: DeliveryTransactionFilters): Promise<DeliveryProviderTransaction[]> {
    let q = anyFrom(this.supabase)('delivery_provider_transactions')
      .select('*').eq('provider_id', filters.providerId);
    if (filters.status) q = q.eq('status', filters.status);
    if (filters.kind) q = q.eq('kind', filters.kind);
    if (filters.shipmentId) q = q.eq('shipment_id', filters.shipmentId);
    q = q.order('occurred_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });
    if (filters.limit) q = q.limit(filters.limit);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map(mapTransaction);
  }

  async createTransaction(input: CreateDeliveryTransactionInput): Promise<DeliveryProviderTransaction> {
    const { data, error } = await anyFrom(this.supabase)('delivery_provider_transactions')
      .insert({
        provider_id: input.providerId,
        shipment_id: input.shipmentId ?? null,
        external_ref: input.externalRef ?? null,
        kind: input.kind,
        amount: input.amount,
        status: input.status,
        occurred_at: input.occurredAt ?? null,
        raw: input.raw ?? null,
      })
      .select().single();
    if (error) throw error;
    return mapTransaction(data);
  }

  async markTransactionsPaidOut(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await anyFrom(this.supabase)('delivery_provider_transactions')
      .update({ status: 'paid_out' }).in('id', ids);
    if (error) throw error;
  }

  async setTransactionStatus(id: string, status: DeliveryTransactionStatus): Promise<void> {
    const { error } = await anyFrom(this.supabase)('delivery_provider_transactions')
      .update({ status }).eq('id', id);
    if (error) throw error;
  }

  async refreshWalletSummary(providerId: string): Promise<DeliveryWalletSummary> {
    const { data, error } = await anyFrom(this.supabase)('delivery_provider_transactions')
      .select('amount, status, kind').eq('provider_id', providerId);
    if (error) throw error;
    let available = 0, pending = 0, paidOut = 0;
    for (const row of data ?? []) {
      const amt = Number(row.amount ?? 0);
      // COD is what flows into the wallet; fees and adjustments net it out.
      const sign = row.kind === 'fee' || row.kind === 'adjustment' ? -1 : 1;
      const value = sign * amt;
      if (row.status === 'available') available += value;
      else if (row.status === 'pending') pending += value;
      else if (row.status === 'paid_out') paidOut += value;
    }
    await this.setCachedBalance(providerId, available, pending);
    return {
      providerId,
      available,
      pending,
      paidOut,
      asOf: new Date().toISOString(),
    };
  }

  // ---- Logs -------------------------------------------------------------

  async listLogs(filters?: DeliveryLogFilters): Promise<DeliveryProviderLog[]> {
    let q = anyFrom(this.supabase)('delivery_provider_logs').select('*');
    if (filters?.providerId) q = q.eq('provider_id', filters.providerId);
    if (filters?.shipmentId) q = q.eq('shipment_id', filters.shipmentId);
    q = q.order('created_at', { ascending: false }).limit(filters?.limit ?? 100);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map(mapLog);
  }

  async createLog(input: CreateDeliveryLogInput): Promise<DeliveryProviderLog> {
    const { data, error } = await anyFrom(this.supabase)('delivery_provider_logs')
      .insert({
        provider_id: input.providerId ?? null,
        shipment_id: input.shipmentId ?? null,
        direction: input.direction,
        endpoint: input.endpoint ?? null,
        method: input.method ?? null,
        request_body: input.requestBody ?? null,
        response_status: input.responseStatus ?? null,
        response_body: input.responseBody ?? null,
      })
      .select().single();
    if (error) throw error;
    return mapLog(data);
  }

  // ---- Reference data ---------------------------------------------------

  async listCities(providerId: string): Promise<DeliveryProviderCity[]> {
    const { data, error } = await anyFrom(this.supabase)('delivery_provider_cities')
      .select('*').eq('provider_id', providerId).order('name');
    if (error) throw error;
    return (data ?? []).map(mapCity);
  }

  async listDistricts(cityId: string): Promise<DeliveryProviderDistrict[]> {
    const { data, error } = await anyFrom(this.supabase)('delivery_provider_districts')
      .select('*').eq('city_id', cityId).order('name');
    if (error) throw error;
    return (data ?? []).map(mapDistrict);
  }

  async listNeighborhoods(districtId: string): Promise<DeliveryProviderNeighborhood[]> {
    const { data, error } = await anyFrom(this.supabase)('delivery_provider_neighborhoods')
      .select('*').eq('district_id', districtId).order('name');
    if (error) throw error;
    return (data ?? []).map(mapNeighborhood);
  }

  async listPickups(providerId: string): Promise<DeliveryProviderPickup[]> {
    const { data, error } = await anyFrom(this.supabase)('delivery_provider_pickups')
      .select('*').eq('provider_id', providerId).order('pickup_index');
    if (error) throw error;
    return (data ?? []).map(mapPickup);
  }

  async replaceCities(input: ReplaceCitiesInput): Promise<void> {
    const { error: delErr } = await anyFrom(this.supabase)('delivery_provider_cities')
      .delete().eq('provider_id', input.providerId);
    if (delErr) throw delErr;
    if (input.cities.length === 0) return;
    const rows = input.cities.map(c => ({
      provider_id: input.providerId,
      external_id: c.externalId,
      name: c.name,
      name_ar: c.nameAr ?? null,
    }));
    const { error: insErr } = await anyFrom(this.supabase)('delivery_provider_cities').insert(rows);
    if (insErr) throw insErr;
  }

  async replaceDistricts(input: ReplaceDistrictsInput): Promise<void> {
    const { data: cityRow, error: cErr } = await anyFrom(this.supabase)('delivery_provider_cities')
      .select('id').eq('provider_id', input.providerId).eq('external_id', input.cityExternalId).maybeSingle();
    if (cErr) throw cErr;
    if (!cityRow) throw new Error(`City external_id=${input.cityExternalId} not cached for provider ${input.providerId}`);
    const cityId = cityRow.id as string;

    const { error: delErr } = await anyFrom(this.supabase)('delivery_provider_districts')
      .delete().eq('city_id', cityId);
    if (delErr) throw delErr;
    if (input.districts.length === 0) return;
    const rows = input.districts.map(d => ({
      provider_id: input.providerId,
      city_id: cityId,
      external_id: d.externalId,
      name: d.name,
    }));
    const { error: insErr } = await anyFrom(this.supabase)('delivery_provider_districts').insert(rows);
    if (insErr) throw insErr;
  }

  async replaceNeighborhoods(input: ReplaceNeighborhoodsInput): Promise<void> {
    const { data: distRow, error: dErr } = await anyFrom(this.supabase)('delivery_provider_districts')
      .select('id').eq('provider_id', input.providerId).eq('external_id', input.districtExternalId).maybeSingle();
    if (dErr) throw dErr;
    if (!distRow) throw new Error(`District external_id=${input.districtExternalId} not cached for provider ${input.providerId}`);
    const districtId = distRow.id as string;

    const { error: delErr } = await anyFrom(this.supabase)('delivery_provider_neighborhoods')
      .delete().eq('district_id', districtId);
    if (delErr) throw delErr;
    if (input.neighborhoods.length === 0) return;
    const rows = input.neighborhoods.map(n => ({
      provider_id: input.providerId,
      district_id: districtId,
      external_id: n.externalId,
      name: n.name,
    }));
    const { error: insErr } = await anyFrom(this.supabase)('delivery_provider_neighborhoods').insert(rows);
    if (insErr) throw insErr;
  }

  async replacePickups(input: ReplacePickupsInput): Promise<void> {
    const { error: delErr } = await anyFrom(this.supabase)('delivery_provider_pickups')
      .delete().eq('provider_id', input.providerId);
    if (delErr) throw delErr;
    if (input.pickups.length === 0) return;
    const rows = input.pickups.map(p => ({
      provider_id: input.providerId,
      pickup_index: p.pickupIndex,
      street_address: p.streetAddress ?? null,
      city_name: p.cityName ?? null,
      district_name: p.districtName ?? null,
      has_gps: p.hasGps ?? false,
    }));
    const { error: insErr } = await anyFrom(this.supabase)('delivery_provider_pickups').insert(rows);
    if (insErr) throw insErr;
  }
}

// ---- Row mappers --------------------------------------------------------

function mapProvider(row: Record<string, unknown>): DeliveryProvider {
  return {
    id: row['id'] as string,
    code: row['code'] as DeliveryProvider['code'],
    displayName: row['display_name'] as string,
    isEnabled: Boolean(row['is_enabled']),
    isDefault: Boolean(row['is_default']),
    apiKey: (row['api_key'] as string | null) ?? null,
    webhookSecret: (row['webhook_secret'] as string | null) ?? null,
    businessId: (row['business_id'] as string | null) ?? null,
    defaultPickupIndex: row['default_pickup_index'] == null ? null : Number(row['default_pickup_index']),
    defaultFee: Number(row['default_fee'] ?? 0),
    defaultPackageSize: Number(row['default_package_size'] ?? 1),
    config: (row['config'] as Record<string, unknown>) ?? {},
    cachedBalanceAvailable: Number(row['cached_balance_available'] ?? 0),
    cachedBalancePending: Number(row['cached_balance_pending'] ?? 0),
    cachedBalanceAt: (row['cached_balance_at'] as string | null) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapTransaction(row: Record<string, unknown>): DeliveryProviderTransaction {
  return {
    id: row['id'] as string,
    providerId: row['provider_id'] as string,
    shipmentId: (row['shipment_id'] as string | null) ?? null,
    externalRef: (row['external_ref'] as string | null) ?? null,
    kind: row['kind'] as DeliveryProviderTransaction['kind'],
    amount: Number(row['amount'] ?? 0),
    status: row['status'] as DeliveryProviderTransaction['status'],
    occurredAt: (row['occurred_at'] as string | null) ?? null,
    raw: (row['raw'] as Record<string, unknown> | null) ?? null,
    createdAt: row['created_at'] as string,
  };
}

function mapLog(row: Record<string, unknown>): DeliveryProviderLog {
  return {
    id: row['id'] as string,
    providerId: (row['provider_id'] as string | null) ?? null,
    shipmentId: (row['shipment_id'] as string | null) ?? null,
    direction: row['direction'] as DeliveryProviderLog['direction'],
    endpoint: (row['endpoint'] as string | null) ?? null,
    method: (row['method'] as string | null) ?? null,
    requestBody: row['request_body'] ?? null,
    responseStatus: (row['response_status'] as number | null) ?? null,
    responseBody: row['response_body'] ?? null,
    createdAt: row['created_at'] as string,
  };
}

function mapCity(row: Record<string, unknown>): DeliveryProviderCity {
  return {
    id: row['id'] as string,
    providerId: row['provider_id'] as string,
    externalId: Number(row['external_id']),
    name: row['name'] as string,
    nameAr: (row['name_ar'] as string | null) ?? null,
  };
}

function mapDistrict(row: Record<string, unknown>): DeliveryProviderDistrict {
  return {
    id: row['id'] as string,
    providerId: row['provider_id'] as string,
    cityId: row['city_id'] as string,
    externalId: Number(row['external_id']),
    name: row['name'] as string,
  };
}

function mapNeighborhood(row: Record<string, unknown>): DeliveryProviderNeighborhood {
  return {
    id: row['id'] as string,
    providerId: row['provider_id'] as string,
    districtId: row['district_id'] as string,
    externalId: Number(row['external_id']),
    name: row['name'] as string,
  };
}

function mapPickup(row: Record<string, unknown>): DeliveryProviderPickup {
  return {
    id: row['id'] as string,
    providerId: row['provider_id'] as string,
    pickupIndex: Number(row['pickup_index']),
    streetAddress: (row['street_address'] as string | null) ?? null,
    cityName: (row['city_name'] as string | null) ?? null,
    districtName: (row['district_name'] as string | null) ?? null,
    hasGps: Boolean(row['has_gps']),
  };
}
